import type { Speaker, Vendor } from "src/types";
import { BlockVariant } from "src/types";

import Button from "@/components/editor/primitives/button/button-component";
import Image from "@/components/editor/primitives/image/image-component";
import {
  DEFAULT_GOLDEN_SPONSORS,
  DEFAULT_SCHEDULES,
  DEFAULT_SILVER_SPONSORS,
} from "@/components/editor/shared/constants";
import { VIDEO_AREA_SIZE_SMALL_TYPE } from "@/components/editor/shared/media/video-with-title-settings";
import { DEFAULT_NAVIGATION_LINKS } from "@/components/editor/shared/navigation";
import {
  createThemeTemplateFactory,
  syncNavigationLinks,
} from "@/components/editor/themes/utils";
import { conditionalShowList } from "@/hooks/use-widget-deprecation";
import getInitialItemsOrDefault from "@/utils/get-initial-items-or-default";

import BoothCard from "./blocks/expo/booth-card/booth-card-component";
import Booths from "./blocks/expo/booths-component";
import { DEFAULT_VENDORS } from "./blocks/expo/constants";
import SyncedBooths from "./blocks/expo/synced-booths-component";
import HeaderOption3 from "./blocks/headers/header-option-3/header-option-3-component";
import VideoWithTitle from "./blocks/media/video-with-title/video-with-title-component";
import StandaloneNavigation from "./blocks/navigations/standalone-navigation/standalone-navigation-component";
import DefaultSchedule from "./blocks/schedules/default-schedule/default-schedule-component";
import SyncedDefaultSchedule from "./blocks/schedules/default-schedule/synced-default-schedule-component";
import AboutDetails from "./blocks/sections/about-details/about-details-component";
import Gallery from "./blocks/sections/gallery";
import { DEFAULT_SPEAKERS } from "./blocks/speakers/constants";
import SpeakerCard from "./blocks/speakers/speaker-card/speaker-card-component";
import Speakers from "./blocks/speakers/speakers-component";
import SyncedSpeakers from "./blocks/speakers/synced-speakers-component";
import SponsorCard from "./blocks/sponsors/sponsor-card/sponsor-card-component";
import Sponsors from "./blocks/sponsors/sponsors-component";
import SyncedSponsors from "./blocks/sponsors/synced-sponsors-component";

export default createThemeTemplateFactory(
  (options) =>
    [
      <StandaloneNavigation key="standalone-navigation" showCta isSticky>
        {DEFAULT_NAVIGATION_LINKS.map(({ label, action }) => (
          <Button isLink key={label} text={label} action={action} />
        ))}
      </StandaloneNavigation>,
      <HeaderOption3
        key="header-option-3"
        backgroundImage="/canvas/nolan/images/background.09b6f242.jpg"
        isLogoShowing
        logoSize={50}
        imageFit="cover"
      />,
      <AboutDetails key="about-details" />,
      ...conditionalShowList(BlockVariant.MANUAL, options, [
        <Speakers key="speakers">
          {getInitialItemsOrDefault<Speaker>(
            options.speakers,
            8,
            DEFAULT_SPEAKERS
          ).map((speaker) => (
            <SpeakerCard
              key={speaker.id + speaker.name}
              speaker={speaker}
              isBioAvailable={!!speaker.bio}
            />
          ))}
        </Speakers>,
        <DefaultSchedule
          key="schedule"
          schedules={
            options.schedules.length > 0 ? options.schedules : DEFAULT_SCHEDULES
          }
        />,
      ]),
      ...conditionalShowList(BlockVariant.SYNCED, options, [
        <SyncedSpeakers key="synced-speakers" />,
        <SyncedDefaultSchedule key="synced-schedule" />,
      ]),
      <Gallery key="gallery">
        <Image src="/canvas/shared/images/sample-1.971c7ea0.jpg" />
        <Image src="/canvas/shared/images/sample-2.f08a4422.jpg" />
        <Image src="/canvas/shared/images/sample-3.4061fb7d.jpg" />
        <Image src="/canvas/shared/images/sample-4.7365ade5.jpg" />
        <Image src="/canvas/shared/images/sample-5.32bfda46.jpg" />
      </Gallery>,
      ...conditionalShowList(BlockVariant.MANUAL, options, [
        <Sponsors key="golden-sponsors" layout="golden">
          {getInitialItemsOrDefault(
            options.sponsors,
            3,
            DEFAULT_GOLDEN_SPONSORS
          ).map((sponsor, idx) => (
            <SponsorCard sponsor={sponsor} key={idx} />
          ))}
        </Sponsors>,
        <Sponsors key="silver-sponsors" layout="silver">
          {getInitialItemsOrDefault(
            options.sponsors,
            4,
            DEFAULT_SILVER_SPONSORS
          ).map((sponsor, idx) => (
            <SponsorCard sponsor={sponsor} key={idx} />
          ))}
        </Sponsors>,
        <Booths key="booths">
          {getInitialItemsOrDefault<Vendor>(
            options.vendors,
            6,
            DEFAULT_VENDORS
          ).map((vendor) => (
            <BoothCard key={vendor.id} vendor={vendor} />
          ))}
        </Booths>,
      ]),
      ...conditionalShowList(BlockVariant.SYNCED, options, [
        <SyncedSponsors key="synced-golden-sponsors" layout="golden" />,
        <SyncedSponsors key="synced-silver-sponsors" layout="silver" />,
        <SyncedBooths key="synced-booths" />,
      ]),
      <VideoWithTitle key="video" videoAreaSize={VIDEO_AREA_SIZE_SMALL_TYPE} />,
    ].filter((e) => Boolean(e)),
  (nodeTrees, query) => syncNavigationLinks(nodeTrees, query)
);
